import React, { useState, useEffect, useRef, useCallback } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { getPostLoginLanding } from "../utils/postLoginLanding";
import { Book, Gamepad2, Compass, Star, Shield, X, Menu, Volume2, VolumeX, Music, SkipForward, LogIn, UserPlus, Settings, User, Users, ChevronDown, ChevronLeft, LogOut, CreditCard, Bell, Eye, EyeOff, Flame, Trophy, Target, Sparkles, CheckCheck, HelpCircle, Palette, Lock, Crown, Award, Share2, Wand2, BarChart3, LifeBuoy, Rocket } from "lucide-react";
import { useApp, IMG } from "../context";
import { isAdminUser } from "../utils/adminCheck";
import ThemePicker from "./ThemePicker";
import CopyShareLinkButton from "./CopyShareLinkButton";
import axios from "axios";
import { formatApiDetail } from "../utils/showApiError";
import { stashNextForOAuth } from "../utils/oauthNext";
import { getStickerEmoji } from "./StickerPicker";
import { NotificationBell } from "./header/NotificationBell";
import { FriendsMenu } from "./header/FriendsMenu";
import SocialNotifier from "./SocialNotifier";
import FriendProfileHost from "./FriendProfileHost";
import HomeworkDueBadge from "./header/HomeworkDueBadge";
import { WhatsNewBadge } from "./header/WhatsNewBadge";
import LearnerHeaderChip from "./LearnerHeaderChip";
import { useFounderCosmetics } from "../hooks/useFounderCosmetics";
import { avatarAura, BADGE_META, SIZE_SCALE } from "../lib/founderCosmetics";
import FounderPowerBar from "./FounderPowerBar";
import FounderStickers from "./FounderStickers";
import { getFrameStyle, getFrameBackdropStyle, FrameScene, animationsOn } from "./avatarFrames";

const API = process.env.REACT_APP_BACKEND_URL;

const TRACK_NAMES = {
  "hip-hop-vibes": "Hip-Hop Vibes",
  "pop-bounce": "Pop Bounce",
  "trap-energy": "Trap Energy",
  "edm-drop": "EDM Drop",
  "chill-groove": "Chill Groove",
  "funk-party": "Funk Party",
};

const Header = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [loginOpen, setLoginOpen] = useState(false);
  const [loginEmail, setLoginEmail] = useState("");
  const [loginPass, setLoginPass] = useState("");
  const [loginError, setLoginError] = useState("");
  const [loginLoading, setLoginLoading] = useState(false);
  const [showPass, setShowPass] = useState(false);
  const { user, token, login, logout, sounds } = useApp();
  const { settings: ccSettings } = useFounderCosmetics();
  const ccAura = ccSettings ? avatarAura(ccSettings.active_glow, ccSettings.active_frame) : {};
  const ccBadge = ccSettings ? BADGE_META[ccSettings.active_badge] : null;
  const ccAvatarClass = ccSettings ? `${ccSettings.active_fly ? "cc-fly" : ""} ${ccSettings.active_godmode ? "cc-godmode" : ""}`.trim() : "";
  const ccAvatarScale = ccSettings ? (SIZE_SCALE[ccSettings.active_size] || 1) : 1;
  const location = useLocation();
  const navigate = useNavigate();
  const isHome = location.pathname === "/";
  const profileRef = useRef(null);
  const loginRef = useRef(null);
  const headerRef = useRef(null);
  // Track the drawer's open state in a ref so the ResizeObserver below can read
  // the live value without re-subscribing.
  const menuOpenRef = useRef(false);

  // Sync real header height to the --app-header-h CSS variable so any toast
  // using `.below-header-fixed` stays clear of the nav even if the header
  // grows/shrinks at different breakpoints.
  //
  // IMPORTANT: the mobile hamburger drawer renders INSIDE <header>, so when it's
  // open the header measures ~full-screen. We must NOT write that inflated
  // height into --app-header-h, because `.header-universe { min-height: var(
  // --app-header-h) }` would then trap the header at full height even after the
  // drawer closes — leaving the dark fixed header covering the whole page on
  // every route (user-reported "everything goes dark after using the top menu").
  // So we skip updates while the drawer is open and re-measure once it closes.
  useEffect(() => {
    const el = headerRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const apply = () => {
      if (menuOpenRef.current) return; // ignore the drawer-inflated height
      const h = Math.round(el.getBoundingClientRect().height);
      if (h > 0) document.documentElement.style.setProperty("--app-header-h", `${h}px`);
    };
    apply();
    const ro = new ResizeObserver(apply);
    ro.observe(el);
    window.addEventListener("resize", apply);
    return () => { ro.disconnect(); window.removeEventListener("resize", apply); };
  }, []);

  // Keep the ref in sync, and re-measure the collapsed header the moment the
  // drawer closes (after it leaves the DOM) so --app-header-h snaps back.
  useEffect(() => {
    menuOpenRef.current = menuOpen;
    if (!menuOpen) {
      requestAnimationFrame(() => {
        const el = headerRef.current;
        if (!el) return;
        const h = Math.round(el.getBoundingClientRect().height);
        if (h > 0) document.documentElement.style.setProperty("--app-header-h", `${h}px`);
      });
    }
  }, [menuOpen]);

  useEffect(() => {
    if (sounds?.musicEnabled && !sounds?.currentTrack) {
      sounds.playMusic("hip-hop-vibes");
    }
  }, [sounds?.musicEnabled]);

  // Mirror the viewer's Sparkle/animation preference to localStorage so the
  // shared avatar renderers (FrameScene, photo bob) can honor it everywhere.
  useEffect(() => {
    try { localStorage.setItem("iw_animations", user?.animations_enabled === false ? "0" : "1"); } catch { /* ignore */ }
  }, [user?.animations_enabled]);


  useEffect(() => {
    const handler = (e) => { if (profileRef.current && !profileRef.current.contains(e.target)) setProfileOpen(false); };
    const loginHandler = (e) => { if (loginRef.current && !loginRef.current.contains(e.target)) setLoginOpen(false); };
    document.addEventListener("mousedown", handler);
    document.addEventListener("mousedown", loginHandler);
    return () => { document.removeEventListener("mousedown", handler); document.removeEventListener("mousedown", loginHandler); };
  }, []);

  const handleQuickLogin = async (e) => {
    e.preventDefault();
    setLoginError("");
    setLoginLoading(true);
    try {
      const res = await axios.post(`${API}/api/auth/login`, { email: loginEmail, password: loginPass });
      login(res.data.token, res.data.user);
      setLoginOpen(false);
      setLoginEmail("");
      setLoginPass("");
      // Honor a `?next=<encoded-path>` query if present (set by
      // PrelaunchGate when an unauthed user deep-linked to a protected
      // route). Same-origin only, never cross-domain.
      const params = new URLSearchParams(location.search);
      const nextParam = params.get("next");
      let landing = null;
      if (nextParam) {
        try {
          const decoded = decodeURIComponent(nextParam);
          if (decoded.startsWith("/") && !decoded.startsWith("//")) landing = decoded;
        } catch { /* fall back below */ }
      }
      if (landing) {
        navigate(landing, { replace: true });
      } else if (["/waitlist", "/waitlist-confirmation"].includes(location.pathname)) {
        // Stuck on a frozen-chrome lockdown page → bounce to the normal
        // landing so the user can actually use the site.
        navigate(getPostLoginLanding(res.data.user), { replace: true });
      }
    } catch (err) {
      setLoginError(formatApiDetail(err.response?.data?.detail, "Invalid email or password"));
    } finally {
      setLoginLoading(false);
    }
  };

  const getInitials = () => {
    if (!user) return "?";
    const first = (user.first_name || user.name || user.explorer_name || "")[0] || "";
    const last = (user.last_name || "")[0] || "";
    return (first + last).toUpperCase() || "?";
  };

  // Prefer the user's chosen sticker emoji over the initials inside the
  // avatar circle. Falls back to initials when the user hasn't picked a
  // sticker yet (or the sticker ID is unknown). Returns `null` if the
  // user shouldn't see any avatar content (e.g., no `user`).
  const getAvatarSticker = () => getStickerEmoji(user?.sticker);

  // Fire the sticker-in-motion pop animation exactly once per session per
  // sticker ID so it feels earned the first time the user sees it after
  // picking/updating, but isn't annoying on every navigation.
  const [stickerPop, setStickerPop] = useState(false);
  useEffect(() => {
    if (!user?.sticker) return;
    const key = `iw_sticker_popped_${user.id || "anon"}_${user.sticker}`;
    let seen = false;
    try { seen = !!sessionStorage.getItem(key); } catch { /* ignore */ }
    if (seen) return;
    setStickerPop(true);
    try { sessionStorage.setItem(key, "1"); } catch { /* ignore */ }
    // Remove the animation class after it completes so re-renders don't
    // keep re-firing (CSS `animation` only plays once on initial mount
    // but React's re-render churn can still blow it away).
    const t = setTimeout(() => setStickerPop(false), 900);
    return () => clearTimeout(t);
  }, [user?.sticker, user?.id]);

  const getUserPhoto = () => {
    return user?.profile_photo || user?.google_picture || null;
  };

  // Avatar preference: the sticker a user picks at setup is the DEFAULT and
  // always wins, a Google sign-in photo must never silently replace it. Users
  // can opt into showing their Google photo via a toggle in the profile menu.
  const [useGooglePhoto, setUseGooglePhoto] = useState(() => {
    try { return localStorage.getItem("iw_avatar_pref") === "google"; } catch { return false; }
  });
  const toggleAvatarPref = () => {
    setUseGooglePhoto((v) => {
      const nv = !v;
      try { localStorage.setItem("iw_avatar_pref", nv ? "google" : "sticker"); } catch { /* ignore */ }
      return nv;
    });
  };
  const showPhotoFirst = useGooglePhoto && !!getUserPhoto();

  // Equipped AI/photo avatar (set from the AI Avatar Studio). This is the
  // user's real profile picture and should win over the sticker when present.
  const avatarImg = user?.avatar_image_url || null;
  const [picPref, setPicPref] = useState(() => {
    try { return localStorage.getItem("iw_pic_pref") || "auto"; } catch { return "auto"; }
  });
  const choosePicPref = (p) => {
    setPicPref(p);
    try { localStorage.setItem("iw_pic_pref", p); } catch { /* ignore */ }
  };
  // Resolve which picture to show: an explicit choice wins (when that source
  // exists); otherwise auto-prefer avatar photo → sticker → google → initials.
  const resolvedPic = (() => {
    if (picPref === "avatar" && avatarImg) return "avatar";
    if (picPref === "sticker" && getAvatarSticker()) return "sticker";
    if (picPref === "google" && getUserPhoto()) return "google";
    if (avatarImg) return "avatar";
    if (getAvatarSticker()) return "sticker";
    if (getUserPhoto()) return "google";
    return "initials";
  })();
  const renderFace = (dimClass) => {
    // Unify with FriendFace everywhere: the equipped frame's ring + backdrop
    // glow + animated scene apply to EVERY avatar variant (photo, sticker,
    // google photo, initials) so the header matches the dashboard/parent corner.
    const frame = user?.avatar_frame;
    const hasFrame = frame && frame !== "none";
    const framed = hasFrame ? { ...getFrameStyle(frame), ...getFrameBackdropStyle(frame) } : null;
    if (resolvedPic === "avatar") return (
      <span className={`${dimClass} rounded-full inline-flex items-center justify-center overflow-hidden border-2 border-emerald-400/50 relative`} style={{ ...getFrameStyle(frame), ...getFrameBackdropStyle(frame) }} data-testid="header-avatar-wrap">
        <FrameScene frameId={frame} />
        <img src={avatarImg} alt="Profile" className="w-full h-full object-cover rounded-full relative" style={{ animation: animationsOn() ? "iwBob 3.6s ease-in-out infinite" : "none" }} data-testid="header-avatar-image" />
      </span>
    );
    if (resolvedPic === "sticker") return (
      <div className={`${dimClass} rounded-full flex items-center justify-center text-xl border-2 border-emerald-400/50 relative overflow-hidden`} style={framed || { background: "linear-gradient(135deg, #3B82F6, #8B5CF6)" }} title="Your sticker" data-testid="header-avatar-sticker">
        <FrameScene frameId={frame} />
        <span aria-hidden className={`relative ${stickerPop ? "iw-sticker-pop" : ""}`}>{getAvatarSticker()}</span>
      </div>
    );
    if (resolvedPic === "google") return (
      <span className={`${dimClass} rounded-full inline-flex items-center justify-center overflow-hidden border-2 border-emerald-400/50 relative`} style={{ ...getFrameStyle(frame), ...getFrameBackdropStyle(frame) }} data-testid="header-avatar-google">
        <FrameScene frameId={frame} />
        <img src={getUserPhoto()} alt="Profile" loading="lazy" className="w-full h-full object-cover rounded-full relative" />
      </span>
    );
    return (
      <div className={`${dimClass} rounded-full flex items-center justify-center text-sm font-fredoka font-bold text-white border-2 border-emerald-400/50 relative overflow-hidden`} style={framed || { background: "linear-gradient(135deg, #3B82F6, #8B5CF6)" }} data-testid="header-avatar-initials">
        <FrameScene frameId={frame} />
        <span className="relative">{getInitials()}</span>
      </div>
    );
  };

  // Solo Explorer unclaimed bonus, small pill in the avatar menu nudging
  // incomplete profiles to finish setup. Reward escalates over time:
  //   · 0-2 days → 50 Stars (welcome)
  //   · 3-6 days → 50 Stars (reminder)
  //   · 7+ days  → 100 Stars + Profile Pioneer badge (final)
  // Clicking the pill dispatches "isaiah:show-solo-nudge" which forces the
  // SoloExplorerProfileNudge component to open into its expanded form,
  // bypassing the per-stage snooze cooldown.
  const getSoloBonus = () => {
    if (!user || user.isDemo) return null;
    if ((user.account_type || "").toLowerCase() !== "solo_explorer") return null;
    if (user.profile_setup_complete === true) return null;
    let days = 0;
    if (user.created_at) {
      try {
        const t = new Date(user.created_at).getTime();
        if (!isNaN(t)) days = Math.max(0, Math.floor((Date.now() - t) / 86400000));
      } catch (_) {}
    }
    if (days >= 7) return { stars: 100, stage: "final", badge: true };
    if (days >= 3) return { stars: 50, stage: "reminder" };
    return { stars: 50, stage: "welcome" };
  };
  const soloBonus = getSoloBonus();
  const openSoloNudge = () => {
    setProfileOpen(false);
    try { window.dispatchEvent(new CustomEvent("isaiah:show-solo-nudge")); } catch (_) {}
  };

  // Isaiah's Command Center — show the dropdown entry ONLY when the BACKEND
  // confirms this account is the founder child (GET /api/founder/status → 200).
  // Never gated on a raw client flag; every backend endpoint re-checks anyway.
  // Cached per session to avoid re-firing on every header mount.
  const [ccEnabled, setCcEnabled] = useState(false);
  const [ccApprover, setCcApprover] = useState(false);
  const [ccPending, setCcPending] = useState(0);
  // Friends System — heartbeat presence so this signed-in explorer shows as
  // online to friends. The header FriendsMenu handles its own request-count badge.
  useEffect(() => {
    if (!user || user.isDemo) return;
    const uid = user.id || user.email;
    if (!uid) return;
    const beat = () => axios.post(`${API}/api/friends/presence`, { user_id: uid }).catch(() => {});
    beat();
    const id = setInterval(beat, 60000);
    return () => clearInterval(id);
  }, [user?.id, user?.isDemo]);
  useEffect(() => {
    if (!user || !token || user.isDemo) { setCcEnabled(false); setCcApprover(false); return; }
    const key = `iw_cc_${user.id || "anon"}`;
    const akey = `iw_cc_appr_${user.id || "anon"}`;
    let cached = null, acached = null;
    try { cached = sessionStorage.getItem(key); acached = sessionStorage.getItem(akey); } catch (_) {}
    if (cached === "1") { setCcEnabled(true); setCcApprover(acached === "1"); return; }
    if (cached === "0") { setCcEnabled(false); setCcApprover(false); return; }
    let alive = true;
    axios.get(`${API}/api/founder/status`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => {
        if (!alive) return;
        setCcEnabled(true);
        const appr = !!r.data?.is_approver;
        setCcApprover(appr);
        try { sessionStorage.setItem(key, "1"); sessionStorage.setItem(akey, appr ? "1" : "0"); } catch (_) {}
      })
      .catch(() => { if (alive) { setCcEnabled(false); setCcApprover(false); try { sessionStorage.setItem(key, "0"); } catch (_) {} } });
    return () => { alive = false; };
  }, [user?.id, token, user?.isDemo]);

  // Approval alerts — for grown-up approvers (David & Vanessa), poll how many of
  // Isaiah's requests are waiting so we can show a badge. Isaiah does most things
  // himself instantly; only requests that affect OTHERS land in this queue.
  useEffect(() => {
    if (!ccApprover || !token) { setCcPending(0); return; }
    let alive = true;
    const poll = () => axios.get(`${API}/api/founder/parent/pending-count`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => { if (alive) setCcPending(r.data?.pending || 0); })
      .catch(() => {});
    poll();
    const id = setInterval(poll, 45000);
    const onFocus = () => poll();
    window.addEventListener("focus", onFocus);
    window.addEventListener("founder:approvals-changed", onFocus);
    return () => { alive = false; clearInterval(id); window.removeEventListener("focus", onFocus); window.removeEventListener("founder:approvals-changed", onFocus); };
  }, [ccApprover, token]);

  return (
    <>
    <header ref={headerRef} className={`header-universe ${isHome ? "header-transparent" : "header-solid"}`} data-testid="header">
      <div className="max-w-7xl mx-auto px-2 sm:px-4 py-3 flex items-center justify-between gap-1">
        <div className="flex flex-col items-start justify-center shrink-0" style={{ zIndex: 20 }}>
          <Link to="/" className="flex items-center relative" data-testid="header-logo" aria-label="Isaiah's World home">
            <img src={IMG.logo} alt="Isaiah's World" className="h-16 sm:h-24 md:h-28 w-auto shrink-0 pointer-events-none" style={{ background: "transparent", objectFit: "contain", marginTop: "-6px", marginBottom: "-6px" }} />
          </Link>
          {user && (
            <div className="sm:hidden -mt-3 ml-16" data-testid="header-learner-chip-mobile">
              <LearnerHeaderChip />
            </div>
          )}
        </div>

        <nav className="hidden xl:flex items-center gap-5">
          {(() => {
            const LEARN_GAME_PATHS = ["/learn/"];
            const isLearningGame = LEARN_GAME_PATHS.some(p => location.pathname.startsWith(p));
            const isNavActive = (link) => {
              if (link.to === "/learn" && isLearningGame) return true;
              if (link.to === "/arcade" && isLearningGame) return false;
              return location.pathname.startsWith(link.to);
            };
            return [
              { to: "/learn", label: "Learn", color: "text-blue-400", icon: Book },
              { to: "/arcade", label: "Arcade", color: "text-purple-400", icon: Gamepad2 },
              { to: "/adventures", label: "Adventures", color: "text-orange-400", icon: Compass, comingSoon: true },
              { to: "/art-studio", label: "Art Studio", color: "text-pink-400", icon: Palette },
              // Corner link adapts to account type: parents get Parent Corner;
              // solo adults AND kids get Explorer Corner (their non-parent space).
              ...(user && (user.role === "parent" || user.role === "admin" || user.role === "super_admin" || user.account_type === "parent")
                ? [{ to: "/parent-corner", label: "Parent Corner", color: "text-gray-300", icon: Shield }]
                : user
                  ? [{ to: "/explorer-corner", label: "Explorer Corner", color: "text-gray-300", icon: Compass }]
                  : [{ to: "/parent-corner", label: "Parent Corner", color: "text-gray-300", icon: Shield }]),
            ].map((link) => {
              const blocked = link.comingSoon && !isAdminUser(user);
              return blocked ? (
                <button
                  key={link.to}
                  onClick={(e) => e.preventDefault()}
                  className="nav-link-universe flex items-center gap-1.5 opacity-50 cursor-not-allowed relative"
                  data-testid={`nav-${link.label.toLowerCase().replace(/\s/g, "-")}`}
                >
                  <link.icon size={14} className="opacity-70" />
                  {link.label}
                  <span className="absolute -top-1.5 -right-3 px-1 py-px rounded text-[7px] font-bold uppercase tracking-wider bg-orange-500/20 text-orange-400 border border-orange-500/20 leading-none">Soon</span>
                </button>
              ) : (
                <Link
                  key={link.to}
                  to={link.to}
                  className={`nav-link-universe flex items-center gap-1.5 relative ${isNavActive(link) ? link.color + " active" : ""}`}
                  data-testid={`nav-${link.label.toLowerCase().replace(/\s/g, "-")}`}
                >
                  <link.icon size={14} className="opacity-70" />
                  {link.label}
                  {link.to === "/learn" && <HomeworkDueBadge />}
                </Link>
              );
            });
          })()}
        </nav>

        <div className="flex items-center gap-1 sm:gap-2 shrink-0">
          {/* Sound + Music, desktop only on mobile we surface them inside the
              hamburger drawer to keep the top bar uncluttered (Issue #4 fix
              May 2026). The bell + avatar stay visible because notifications
              are time-sensitive and the avatar is the primary nav surface. */}
          {sounds && (
            <div className="hidden xl:flex items-center gap-1">
              <button onClick={sounds.toggleSound}
                className={`p-1.5 rounded-lg transition-all ${sounds.soundEnabled ? "text-white/70 hover:text-white" : "text-white/30 hover:text-white/50"}`}
                title={sounds.soundEnabled ? "Mute Sound Effects" : "Enable Sound Effects"}
                data-testid="sound-toggle">
                {sounds.soundEnabled ? <Volume2 size={16} /> : <VolumeX size={16} />}
              </button>
              <button onClick={() => { sounds.toggleMusic(); if (!sounds.musicEnabled) sounds.playMusic("hip-hop-vibes"); }}
                className={`hidden xl:inline-flex p-1.5 rounded-lg transition-all ${sounds.musicEnabled ? "text-green-400 hover:text-green-300" : "text-white/30 hover:text-white/50"}`}
                title={sounds.musicEnabled ? "Stop Music" : "Play Music"}
                data-testid="music-toggle">
                <Music size={16} />
              </button>
              {sounds.musicEnabled && sounds.currentTrack && (
                <div className="hidden xl:flex items-center gap-1">
                  <span className="text-white/40 text-[9px] hidden sm:block max-w-[80px] truncate" data-testid="current-track-name">
                    {TRACK_NAMES[sounds.currentTrack] || sounds.currentTrack}
                  </span>
                  <button onClick={sounds.nextTrack}
                    className="p-1 rounded-lg text-white/40 hover:text-white/70 transition-all"
                    title="Next Track" data-testid="next-track-btn">
                    <SkipForward size={13} />
                  </button>
                </div>
              )}
            </div>
          )}

          <div className="hidden xl:inline-flex"><ThemePicker /></div>

          {user && !user.isDemo ? (
            <>
              <div className="hidden sm:flex"><LearnerHeaderChip /></div>
              <WhatsNewBadge token={token} />
              <NotificationBell user={user} token={token} />
              <SocialNotifier user={user} />
              <FriendsMenu user={user} />
              {(() => {
                // Founder status is derived defensively from BOTH `founding_member`
                // AND `tier_name` because historically the two could drift apart
                // (parents vs explorer_profiles). The dropdown below uses the same
                // predicate so the badge and dropdown label never contradict.
                const isFounder = Boolean(user.founding_member) || (typeof user.tier_name === "string" && user.tier_name.toLowerCase().startsWith("founder"));
                if (!isFounder) return null;
                return (
                  // Persistent founder badge, visible on every page in the header.
                  // Tooltip + optional click targets settings so founders can always
                  // see and verify their status without hunting through menus.
                  <Link
                    to="/settings"
                    className="hidden xl:inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-gradient-to-r from-fuchsia-500/25 to-amber-500/25 border border-fuchsia-400/50 text-[11px] font-fredoka font-bold tracking-wider uppercase shadow-md shadow-fuchsia-500/20 hover:from-fuchsia-500/40 hover:to-amber-500/40 transition-all"
                    style={{ color: "#FDE68A" }}
                    title={user.founding_position ? `You're Founder #${user.founding_position}, lifetime launch cohort access` : "You're a Founder, lifetime launch cohort access"}
                    data-testid="founder-badge"
                  >
                    <Crown size={12} strokeWidth={2.5} className="text-amber-200" />
                    <span>Founder{user.founding_position ? ` #${user.founding_position}` : ""}</span>
                  </Link>
                );
              })()}
              {/* One-click "Copy my share link", desktop top bar only.
                  On mobile the share affordance lives inside the hamburger
                  drawer to keep the top bar uncluttered (May 2026 mobile
                  header cleanup). Visible to every signed-in account. */}
              <div className="hidden xl:inline-flex"><CopyShareLinkButton /></div>
              <Link to="/my-world" className="btn-universe-sm hidden sm:inline-flex" data-testid="my-world-btn">Dashboard</Link>

              {/* Profile Avatar Circle + Dropdown */}
              <div className="relative" ref={profileRef}>
                <button onClick={() => setProfileOpen(!profileOpen)}
                  className="relative flex items-center gap-1 sm:gap-2 p-0.5 sm:p-1 rounded-full hover:ring-2 hover:ring-white/20 transition-all shrink-0"
                  data-testid="profile-avatar-btn">
                  <span className={`relative inline-flex rounded-full ${ccAvatarClass}`} style={{ ...ccAura, transform: ccAvatarScale !== 1 ? `scale(${ccAvatarScale})` : undefined }} data-testid={ccSettings ? "founder-avatar-aura" : undefined}>
                  {renderFace("w-9 h-9")}
                  {ccBadge && (
                    <span className="absolute -bottom-1 -left-1 text-[13px] leading-none drop-shadow" title={ccBadge.label} data-testid="founder-avatar-badge">{ccBadge.emoji}</span>
                  )}
                  {ccApprover && ccPending > 0 && (
                    <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 border-2 border-[#0b1020] text-white text-[10px] font-bold flex items-center justify-center" title={`${ccPending} request${ccPending > 1 ? "s" : ""} waiting`} data-testid="avatar-cc-approvals-dot">{ccPending}</span>
                  )}
                  </span>
                  {/* Unclaimed-bonus dot for Solo Explorers with incomplete
                      profiles. Gold = day 0-6, amber pulse = day 7+ (final
                      stage) to pull their eye. */}
                  {soloBonus && (
                    <span
                      className={`absolute top-0 right-6 w-3 h-3 rounded-full border-2 border-slate-900 ${soloBonus.stage === "final" ? "bg-amber-300 animate-pulse" : "bg-amber-400"}`}
                      title={`Unclaimed bonus: ${soloBonus.stars} Stars`}
                      data-testid="avatar-unclaimed-dot"
                    />
                  )}
                  <ChevronDown size={12} className={`text-white/50 transition-transform ${profileOpen ? "rotate-180" : ""}`} />
                </button>

                {profileOpen && (
                  <div
                    className="
                      fixed left-2 right-2 top-[64px] z-50 overflow-hidden
                      sm:absolute sm:left-auto sm:right-0 sm:top-full sm:mt-2 sm:w-64
                      max-h-[calc(100dvh-var(--app-header-h,120px)-16px)] overflow-y-auto
                      rounded-2xl border-2 border-white/15
                      bg-slate-900 sm:bg-slate-900/95 backdrop-blur-md
                      shadow-2xl shadow-black/60
                    "
                    style={{ backgroundColor: "rgba(15, 23, 42, 0.99)" }}
                    data-testid="profile-dropdown"
                  >
                    {/* Sticky Back / Close bar so people can always exit the
                        dropdown without scrolling (esp. on mobile where it's a
                        full-width panel). */}
                    <div className="sticky top-0 z-10 flex items-center justify-between px-3 py-2 border-b border-white/10 bg-slate-900/95 backdrop-blur-md">
                      <button onClick={() => setProfileOpen(false)} data-testid="profile-dropdown-back"
                        className="inline-flex items-center gap-1.5 rounded-full bg-white/8 hover:bg-white/15 text-white/80 hover:text-white px-3 py-1.5 text-xs font-bold transition-all">
                        <ChevronLeft size={14} /> Back
                      </button>
                      <button onClick={() => setProfileOpen(false)} data-testid="profile-dropdown-close" aria-label="Close menu"
                        className="w-8 h-8 rounded-full bg-white/8 hover:bg-white/15 text-white/70 hover:text-white flex items-center justify-center transition-all">
                        <X size={16} />
                      </button>
                    </div>

                    {/* User Info Header */}
                    <div className="px-4 py-3 border-b border-white/10 flex items-center gap-3">
                      {renderFace("w-10 h-10")}
                      <div className="flex-1 min-w-0">
                        <p className="text-white text-sm font-fredoka font-bold truncate">{user.explorer_name || user.display_name || user.first_name || user.name || "Explorer"}</p>
                        <p className="text-white/40 text-xs truncate">{user.email}</p>
                        {user.founding_member ? (
                          <p className="inline-flex items-center gap-1 text-amber-200 text-[10px] font-bold mt-0.5" data-testid="dropdown-founder-tag">
                            <Crown size={10} className="text-amber-300" />
                            Founder{user.founding_position ? ` #${user.founding_position}` : ""} · Lifetime
                          </p>
                        ) : (
                          <p className="text-emerald-400/70 text-[10px] font-bold">{user.tier_name || "Free Explorer"}</p>
                        )}
                        {ccSettings && (
                          <p className="inline-flex items-center gap-1 text-fuchsia-200 text-[10px] font-bold mt-0.5 capitalize" data-testid="dropdown-cc-title">
                            {ccBadge ? <span aria-hidden>{ccBadge.emoji}</span> : <Sparkles size={10} className="text-fuchsia-300" />}
                            {ccSettings.active_title}
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Unclaimed-bonus banner for Solo Explorers with
                        incomplete profiles. Clicking forces the floating
                        nudge to open directly into its expanded form. */}
                    {soloBonus && (
                      <button
                        onClick={openSoloNudge}
                        className={`w-full px-4 py-2.5 flex items-center gap-2.5 border-b border-white/10 transition-all text-left ${
                          soloBonus.stage === "final"
                            ? "bg-gradient-to-r from-amber-400/20 to-fuchsia-500/20 hover:from-amber-400/30 hover:to-fuchsia-500/30"
                            : "bg-gradient-to-r from-fuchsia-500/12 to-amber-400/12 hover:from-fuchsia-500/20 hover:to-amber-400/20"
                        }`}
                        data-testid="dropdown-unclaimed-bonus"
                      >
                        <div className={`w-7 h-7 rounded-full border flex items-center justify-center shrink-0 ${
                          soloBonus.stage === "final"
                            ? "bg-amber-400/30 border-amber-300/60"
                            : "bg-amber-400/20 border-amber-300/40"
                        }`}>
                          {soloBonus.badge
                            ? <Award size={13} className="text-amber-200" />
                            : <Star size={12} className="text-amber-300" fill="currentColor" />}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-white text-[12px] font-fredoka font-bold leading-tight">
                            Unclaimed bonus: <span className="text-amber-200">{soloBonus.stars} Stars</span>
                            {soloBonus.badge && <span className="text-amber-200"> + badge</span>}
                          </p>
                          <p className="text-white/55 text-[10px] font-nunito leading-tight mt-0.5">
                            {soloBonus.stage === "final"
                              ? "Complete your profile to claim the Profile Pioneer badge"
                              : "Complete your profile · ~30 seconds"}
                          </p>
                        </div>
                        <ChevronDown size={14} className="text-white/40 -rotate-90" />
                      </button>
                    )}

                    {/* Menu Items */}
                    <div className="py-1">
                      {/* Profile picture source chooser */}
                      <div className="px-4 py-2.5 border-b border-white/5" data-testid="dropdown-pic-source">
                        <p className="text-white/50 text-[10px] font-bold uppercase tracking-wide mb-2">Profile picture</p>
                        <div className="flex gap-1.5">
                          {getAvatarSticker() && (
                            <button onClick={() => choosePicPref("sticker")} data-testid="pic-source-sticker"
                              className={`flex-1 py-1.5 rounded-lg text-[11px] font-bold border transition ${resolvedPic === "sticker" ? "bg-emerald-500/20 border-emerald-400/50 text-white" : "bg-white/[0.04] border-white/10 text-white/60 hover:bg-white/[0.08]"}`}>
                              Sticker
                            </button>
                          )}
                          {avatarImg && (
                            <button onClick={() => choosePicPref("avatar")} data-testid="pic-source-avatar"
                              className={`flex-1 py-1.5 rounded-lg text-[11px] font-bold border transition ${resolvedPic === "avatar" ? "bg-fuchsia-500/20 border-fuchsia-400/50 text-white" : "bg-white/[0.04] border-white/10 text-white/60 hover:bg-white/[0.08]"}`}>
                              My Avatar
                            </button>
                          )}
                          {getUserPhoto() && (
                            <button onClick={() => choosePicPref("google")} data-testid="pic-source-google"
                              className={`flex-1 py-1.5 rounded-lg text-[11px] font-bold border transition ${resolvedPic === "google" ? "bg-cyan-500/20 border-cyan-400/50 text-white" : "bg-white/[0.04] border-white/10 text-white/60 hover:bg-white/[0.08]"}`}>
                              Google
                            </button>
                          )}
                        </div>
                        <p className="text-white/40 text-[10px] mt-1.5 leading-snug">
                          {avatarImg ? "\u201CMy Avatar\u201D is the look you set in the studio \u2014 a real photo or an animated AI look." : "Make a real-photo or animated look in the studio, then set it here."}
                        </p>
                        <Link to="/avatar/ai" onClick={() => setProfileOpen(false)}
                          className="mt-2 flex items-center gap-1.5 text-fuchsia-300 hover:text-fuchsia-200 text-[11px] font-bold" data-testid="pic-source-studio-link">
                          <Wand2 size={12} /> Create or change my avatar
                        </Link>
                      </div>
                      <Link to="/profile" onClick={() => setProfileOpen(false)}
                        className="flex items-center gap-3 px-4 py-2.5 text-white/80 hover:text-white hover:bg-white/5 transition-all text-sm font-nunito font-bold"
                        data-testid="dropdown-profile-link">
                        <User size={16} className="text-blue-400" /> My Profile
                      </Link>
                      <Link to="/avatar/ai" onClick={() => setProfileOpen(false)}
                        className="flex items-center gap-3 px-4 py-2.5 text-white/80 hover:text-white hover:bg-white/5 transition-all text-sm font-nunito font-bold"
                        data-testid="dropdown-ai-avatar-link">
                        <Wand2 size={16} className="text-fuchsia-400" /> AI Avatar Studio
                      </Link>
                      <Link to="/settings" onClick={() => setProfileOpen(false)}
                        className="flex items-center gap-3 px-4 py-2.5 text-white/80 hover:text-white hover:bg-white/5 transition-all text-sm font-nunito font-bold"
                        data-testid="dropdown-settings-link">
                        <Settings size={16} className="text-white/50" /> Settings
                      </Link>
                      <Link to="/my-world" onClick={() => setProfileOpen(false)}
                        className="flex items-center gap-3 px-4 py-2.5 text-white/80 hover:text-white hover:bg-white/5 transition-all text-sm font-nunito font-bold"
                        data-testid="dropdown-dashboard-link">
                        <Star size={16} className="text-yellow-400" /> My Dashboard
                      </Link>
                      <Link to="/my-referrals" onClick={() => setProfileOpen(false)}
                        className="flex items-center gap-3 px-4 py-2.5 text-white/80 hover:text-white hover:bg-white/5 transition-all text-sm font-nunito font-bold"
                        data-testid="dropdown-referrals-link">
                        <Share2 size={16} className="text-emerald-400" /> Referrals & Rewards
                      </Link>
                      <Link to="/ambassador-dashboard" onClick={() => setProfileOpen(false)}
                        className="flex items-center gap-3 px-4 py-2.5 text-white/80 hover:text-white hover:bg-white/5 transition-all text-sm font-nunito font-bold"
                        data-testid="dropdown-ambassador-dashboard-link">
                        <BarChart3 size={16} className="text-cyan-400" /> Ambassador Dashboard
                      </Link>
                      <Link to="/my-referrals/marketing" onClick={() => setProfileOpen(false)}
                        className="flex items-center gap-3 px-4 py-2.5 text-white/80 hover:text-white hover:bg-white/5 transition-all text-sm font-nunito font-bold"
                        data-testid="dropdown-marketing-link">
                        <Wand2 size={16} className="text-fuchsia-400" /> Marketing Toolkit
                      </Link>
                      <Link to="/subscribe" onClick={() => setProfileOpen(false)}
                        className="flex items-center gap-3 px-4 py-2.5 text-white/80 hover:text-white hover:bg-white/5 transition-all text-sm font-nunito font-bold"
                        data-testid="dropdown-subscription-link">
                        <CreditCard size={16} className="text-emerald-400" /> Subscription
                      </Link>
                      <Link to="/leaderboard" onClick={() => setProfileOpen(false)}
                        className="flex items-center gap-3 px-4 py-2.5 text-white/80 hover:text-white hover:bg-white/5 transition-all text-sm font-nunito font-bold"
                        data-testid="dropdown-leaderboard-link">
                        <Trophy size={16} className="text-yellow-400" /> Leaderboard
                      </Link>
                      <Link to="/game-history" onClick={() => setProfileOpen(false)}
                        className="flex items-center gap-3 px-4 py-2.5 text-white/80 hover:text-white hover:bg-white/5 transition-all text-sm font-nunito font-bold"
                        data-testid="dropdown-history-link">
                        <Gamepad2 size={16} className="text-orange-400" /> Game History
                      </Link>
                      <button onClick={() => { setProfileOpen(false); window.dispatchEvent(new Event("isaiah:start-tour")); }}
                        className="w-full flex items-center gap-3 px-4 py-2.5 text-white/80 hover:text-white hover:bg-white/5 transition-all text-sm font-nunito font-bold text-left"
                        data-testid="dropdown-tour-link">
                        <Compass size={16} className="text-purple-400" /> Take a Tour
                      </button>
                      <Link to="/help" onClick={() => setProfileOpen(false)}
                        className="flex items-center gap-3 px-4 py-2.5 text-white/80 hover:text-white hover:bg-white/5 transition-all text-sm font-nunito font-bold"
                        data-testid="dropdown-help-link">
                        <HelpCircle size={16} className="text-sky-400" /> Help & Support
                      </Link>
                      <Link to="/get-help" onClick={() => setProfileOpen(false)}
                        className="flex items-center gap-3 px-4 py-2.5 text-white/80 hover:text-white hover:bg-white/5 transition-all text-sm font-nunito font-bold"
                        data-testid="dropdown-support-access-link">
                        <LifeBuoy size={16} className="text-indigo-400" /> Allow Support Access
                      </Link>
                      {user && (user.role === "child_explorer" || user.account_type === "solo_explorer") && (
                        <Link to="/switch-to-parent" onClick={() => setProfileOpen(false)}
                          className="flex items-center gap-3 px-4 py-2.5 text-white/80 hover:text-white hover:bg-white/5 transition-all text-sm font-nunito font-bold"
                          data-testid="dropdown-switch-parent-link">
                          <Shield size={16} className="text-violet-400" /> Switch to Parent account
                        </Link>
                      )}
                      {ccEnabled && (
                        <Link to="/isaiah-command-center" onClick={() => setProfileOpen(false)}
                          className="flex items-center gap-3 px-4 py-2.5 mx-1 my-1 rounded-xl bg-gradient-to-r from-fuchsia-500/25 to-amber-400/20 border border-fuchsia-400/40 text-white hover:from-fuchsia-500/40 hover:to-amber-400/30 transition-all text-sm font-fredoka font-bold"
                          data-testid="dropdown-command-center-link">
                          <Rocket size={16} className="text-fuchsia-300" /> Isaiah's Command Center
                          {ccApprover && ccPending > 0 ? (
                            <span className="ml-auto min-w-[20px] h-5 px-1.5 rounded-full bg-red-500 text-white text-[11px] font-bold flex items-center justify-center" data-testid="dropdown-cc-approvals-badge">{ccPending}</span>
                          ) : (
                            <Sparkles size={13} className="ml-auto text-amber-300" />
                          )}
                        </Link>
                      )}
                    </div>

                    {/* Logout */}
                    <div className="border-t border-white/10">
                      <button onClick={() => { logout(); setProfileOpen(false); navigate("/", { replace: true }); }}
                        className="w-full flex items-center gap-3 px-4 py-2.5 text-red-400/80 hover:text-red-400 hover:bg-red-500/5 transition-all text-sm font-nunito font-bold"
                        data-testid="dropdown-logout-btn">
                        <LogOut size={16} /> Log Out
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </>
          ) : (
            <div className="flex items-center gap-2">
              {/* Login is the ONE exception during pre-launch lockdown:
                  when App.js wraps the header in pointer-events:none for
                  the /waitlist page, this inline `pointerEvents: auto`
                  re-enables the button so already-approved founders can
                  still sign in. Sign Up stays locked because the only
                  way to make an account during pre-launch is the
                  waitlist form below. */}
              <div className="relative" ref={loginRef} style={{ pointerEvents: "auto" }} data-waitlist-allow="true">
                <button onClick={() => setLoginOpen(!loginOpen)} className="nav-link-universe text-sm flex items-center gap-1 whitespace-nowrap" data-testid="login-btn">
                  <LogIn size={14} /> Log In
                </button>
                {loginOpen && (
                  <>
                    {/* Mobile backdrop, solid dark overlay so the form
                        is readable over the homepage. Click closes. */}
                    <div
                      onClick={() => setLoginOpen(false)}
                      className="fixed inset-0 bg-slate-950/85 backdrop-blur-sm xl:hidden z-40"
                      style={{ pointerEvents: "auto" }}
                      aria-hidden="true"
                      data-testid="quick-login-backdrop"
                    />
                    <div
                      className="
                        fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[92vw] max-w-sm z-50 md:absolute md:left-auto md:top-full md:right-0 md:translate-x-0 md:translate-y-0 md:mt-2 md:w-80
                        bg-slate-900 border border-white/15 rounded-2xl shadow-2xl shadow-black/80 overflow-hidden
                      "
                      style={{ backgroundColor: "#0f172a", pointerEvents: "auto" }}
                      data-testid="quick-login-modal"
                    >
                    <form onSubmit={handleQuickLogin} className="p-5 space-y-3">
                      <h3 className="text-white font-black text-base font-nunito">Welcome Back</h3>
                      <p className="text-white/40 text-xs -mt-1">Sign in to access your profile</p>

                      {/* Google sign-in, uses the same custom OAuth flow as
                          LoginPage/SignupPage so the consent screen is
                          branded "Sign in to Isaiah's World". Rendered ABOVE
                          the email form so returning Google users don't have
                          to hunt for it.
                          REMINDER: DO NOT HARDCODE THE URL, breaks auth across envs. */}
                      <button
                        type="button"
                        onClick={async () => {
                          const redirectUrl = window.location.origin + "/auth/google";
                          // Preserve any ?next=<path> across the OAuth round-trip.
                          stashNextForOAuth();
                          try {
                            const { data } = await axios.get(`${API}/api/auth/google/custom-login`, {
                              params: { redirect_uri: redirectUrl },
                            });
                            if (data?.auth_url) window.location.href = data.auth_url;
                          } catch (err) {
                            setLoginError(formatApiDetail(err.response?.data?.detail, "Couldn't start Google sign-in. Please try again."));
                          }
                        }}
                        className="w-full flex items-center justify-center gap-2 px-3 py-2.5 rounded-xl border border-white/15 bg-white/5 hover:bg-white/10 text-white font-bold text-sm transition-all"
                        data-testid="quick-login-google-btn"
                      >
                        <svg width="16" height="16" viewBox="0 0 18 18" fill="none" aria-hidden="true">
                          <path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 01-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615z" fill="#4285F4"/>
                          <path d="M9 18c2.43 0 4.467-.806 5.956-2.184l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 009 18z" fill="#34A853"/>
                          <path d="M3.964 10.706A5.41 5.41 0 013.682 9c0-.593.102-1.17.282-1.706V4.962H.957A8.996 8.996 0 000 9c0 1.452.348 2.827.957 4.038l3.007-2.332z" fill="#FBBC05"/>
                          <path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 00.957 4.962L3.964 7.294C4.672 5.166 6.656 3.58 9 3.58z" fill="#EA4335"/>
                        </svg>
                        Sign in with Google
                      </button>
                      <div className="flex items-center gap-2 py-0.5">
                        <div className="flex-1 h-px bg-white/10" />
                        <span className="text-white/30 text-[10px] font-nunito">or</span>
                        <div className="flex-1 h-px bg-white/10" />
                      </div>

                      {loginError && <p className="text-red-400 text-xs bg-red-500/10 px-3 py-1.5 rounded-lg" data-testid="login-error">{loginError}</p>}
                      <input type="text" value={loginEmail} onChange={e => setLoginEmail(e.target.value)} placeholder="Email or username"
                        className="w-full bg-slate-800 border border-white/15 rounded-xl px-3.5 py-2.5 text-sm text-white placeholder-white/30 focus:border-blue-500/50 focus:outline-none transition-colors"
                        data-testid="quick-login-email" required autoFocus
                        autoCapitalize="off" autoCorrect="off" spellCheck="false" />
                      <div className="relative">
                        <input type={showPass ? "text" : "password"} value={loginPass} onChange={e => setLoginPass(e.target.value)} placeholder="Password"
                          className="w-full bg-slate-800 border border-white/15 rounded-xl px-3.5 py-2.5 text-sm text-white placeholder-white/30 focus:border-blue-500/50 focus:outline-none transition-colors pr-10"
                          data-testid="quick-login-password" required />
                        <button type="button" onClick={() => setShowPass(!showPass)} className="absolute right-3 top-1/2 -translate-y-1/2 text-white/30 hover:text-white/60">
                          {showPass ? <EyeOff size={14} /> : <Eye size={14} />}
                        </button>
                      </div>
                      <button type="submit" disabled={loginLoading}
                        className="w-full py-2.5 rounded-xl bg-gradient-to-r from-blue-600 to-purple-600 text-white font-bold text-sm hover:from-blue-500 hover:to-purple-500 transition-all disabled:opacity-50"
                        data-testid="quick-login-submit">
                        {loginLoading ? "Signing in..." : "Sign In"}
                      </button>
                      <p className="text-center -mt-0.5">
                        <Link to="/forgot-password" onClick={() => setLoginOpen(false)} className="text-blue-400/70 hover:text-blue-300 text-[10px] font-bold" data-testid="quick-forgot-password-link">Forgot password?</Link>
                      </p>
                      {/* Hide the "Sign Up" link on lockdown pages
                          (/waitlist, /waitlist-confirmation), during
                          pre-launch the only way to make an account is the
                          waitlist form below; surfacing a Sign Up link
                          here would route around it. */}
                      {!["/waitlist", "/waitlist-confirmation"].includes(location.pathname) && (
                        <p className="text-white/30 text-[10px] text-center">
                          Don't have an account? <Link to="/signup" onClick={() => setLoginOpen(false)} className="text-blue-400 hover:text-blue-300 font-bold">Sign Up</Link>
                        </p>
                      )}
                    </form>
                    </div>
                  </>
                )}
              </div>
              <Link to="/signup" className="btn-universe-sm whitespace-nowrap" data-testid="signup-btn">Sign Up</Link>
            </div>
          )}
          <button className="xl:hidden p-1.5 sm:p-2 rounded-xl text-white/80 hover:text-white transition-colors shrink-0"
            onClick={() => setMenuOpen(!menuOpen)} data-testid="mobile-menu-btn">
            {menuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {menuOpen && (
        <div className="xl:hidden bg-slate-900/95 border-t border-white/10 py-4 px-4 space-y-1 mobile-drawer-anim max-h-[calc(100dvh-64px)] overflow-y-auto pb-40">
          {user ? (
            <>
              {(() => {
                const renderLink = (link) => {
                  const blocked = link.comingSoon && !isAdminUser(user);
                  return blocked ? (
                    <div key={link.to}
                      className="flex items-center gap-3 py-3 px-4 rounded-xl text-white/30 cursor-not-allowed"
                      data-testid={`mobile-nav-${link.label.toLowerCase().replace(/\s/g, "-")}`}>
                      <link.icon size={20} className="text-orange-400/40" /> {link.label}
                      <span className="ml-auto px-2 py-0.5 rounded text-[9px] font-bold uppercase bg-orange-500/15 text-orange-400/70 border border-orange-500/15">Coming Soon</span>
                    </div>
                  ) : (
                    <Link key={link.to} to={link.to}
                      className="flex items-center gap-3 py-3 px-4 rounded-xl text-white/80 hover:bg-white/10 transition-all font-medium"
                      data-testid={`drawer-nav-${link.label.toLowerCase().replace(/\s/g, "-")}`}
                      onClick={() => setMenuOpen(false)}>
                      <link.icon size={20} className={link.color} /> {link.label}
                    </Link>
                  );
                };
                // Corner link adapts to account type (see desktop nav above).
                const cornerLink = (user.role === "parent" || user.role === "admin" || user.role === "super_admin" || user.account_type === "parent")
                  ? { to: "/parent-corner", icon: Shield, label: "Parent Corner", color: "text-gray-400" }
                  : { to: "/explorer-corner", icon: Compass, label: "Explorer Corner", color: "text-gray-400" };
                // Grouped into clear categories with a divider + label between each.
                // NOTE: AI Avatar Studio is intentionally NOT here — it lives in the
                // profile-picture dropdown (2nd, after My Profile). It's rarely-visited,
                // so it would just clutter this nav list.
                const groups = [
                  { key: "explore", label: "Explore", items: [
                    { to: "/learn", icon: Book, label: "Learn", color: "text-blue-400" },
                    { to: "/arcade", icon: Gamepad2, label: "Arcade", color: "text-purple-400" },
                    { to: "/adventures", icon: Compass, label: "Adventures", color: "text-orange-400", comingSoon: true },
                    { to: "/art-studio", icon: Palette, label: "Art Studio", color: "text-pink-400" },
                  ]},
                  { key: "account", label: "Your space", items: [
                    { to: "/friends", icon: Users, label: "Friends", color: "text-violet-400" },
                    { to: "/my-world", icon: Star, label: "Dashboard", color: "text-yellow-400" },
                    // Profile and Settings are SEPARATE — My Profile always opens the
                    // profile, Settings always opens settings.
                    { to: "/profile", icon: User, label: "My Profile", color: "text-blue-400" },
                    { to: "/settings", icon: Settings, label: "Settings", color: "text-white/60" },
                  ]},
                  { key: "more", label: "More", items: [cornerLink, { to: "/help", icon: HelpCircle, label: "Help & Support", color: "text-sky-400" }] },
                ];
                return groups.map((g, gi) => (
                  <div key={g.key} className={gi > 0 ? "mt-2 pt-2 border-t border-white/10" : ""} data-testid={`drawer-group-${g.key}`}>
                    <p className="text-[10px] font-bold uppercase tracking-widest text-white/35 px-4 mb-1 mt-1">{g.label}</p>
                    {g.items.map(renderLink)}
                  </div>
                ));
              })()}

              {/* Isaiah's Command Center intentionally lives ONLY in the profile
                  dropdown (top-right avatar), not this nav drawer. */}

              {/* ---- Mobile-only Quick Toggles ----
                  These live in the hamburger drawer on mobile (and ONLY on
                  mobile, on desktop they're in the persistent top bar).
                  Surfaces sound, music, theme, and share-link affordances
                  without crowding the small-screen header (May 2026). */}
              <div className="mt-3 pt-3 border-t border-white/10">
                <p className="text-[10px] font-bold uppercase tracking-widest text-white/35 px-4 mb-1.5">Quick Toggles</p>

                {sounds && (
                  <>
                    <button
                      onClick={() => sounds.toggleSound()}
                      className="w-full flex items-center gap-3 py-3 px-4 rounded-xl text-white/80 hover:bg-white/10 transition-all font-medium"
                      data-testid="mobile-sound-toggle"
                    >
                      {sounds.soundEnabled ? <Volume2 size={20} className="text-white/70" /> : <VolumeX size={20} className="text-white/40" />}
                      <span>{sounds.soundEnabled ? "Sound effects: On" : "Sound effects: Off"}</span>
                    </button>
                    <button
                      onClick={() => { sounds.toggleMusic(); if (!sounds.musicEnabled) sounds.playMusic("hip-hop-vibes"); }}
                      className="w-full flex items-center gap-3 py-3 px-4 rounded-xl text-white/80 hover:bg-white/10 transition-all font-medium"
                      data-testid="mobile-music-toggle"
                    >
                      <Music size={20} className={sounds.musicEnabled ? "text-emerald-400" : "text-white/40"} />
                      <span className="flex-1 text-left">
                        {sounds.musicEnabled ? "Music: On" : "Music: Off"}
                        {sounds.musicEnabled && sounds.currentTrack && (
                          <span className="block text-[11px] text-white/40 font-normal mt-0.5">{TRACK_NAMES[sounds.currentTrack] || sounds.currentTrack}</span>
                        )}
                      </span>
                      {sounds.musicEnabled && (
                        <span
                          role="button"
                          tabIndex={0}
                          onClick={(e) => { e.stopPropagation(); sounds.nextTrack(); }}
                          onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.stopPropagation(); sounds.nextTrack(); } }}
                          className="p-1.5 rounded-lg text-white/50 hover:text-white/80 hover:bg-white/10 transition-all cursor-pointer"
                          title="Next track"
                          data-testid="mobile-next-track"
                        >
                          <SkipForward size={16} />
                        </span>
                      )}
                    </button>
                  </>
                )}

                {/* Theme picker, its trigger button is itself touch-friendly */}
                <div className="flex items-center gap-3 py-3 px-4 rounded-xl text-white/80" data-testid="mobile-theme-picker-row">
                  <Palette size={20} className="text-fuchsia-400" />
                  <span className="flex-1">Theme</span>
                  <ThemePicker />
                </div>

                {/* Share link, surfaces the share button via the same component */}
                <div className="flex items-center gap-3 py-3 px-4 rounded-xl text-white/80" data-testid="mobile-share-link-row">
                  <Share2 size={20} className="text-cyan-400" />
                  <span className="flex-1">Share my link</span>
                  <CopyShareLinkButton />
                </div>
              </div>
            </>
          ) : (
            <>
              <button className="flex items-center gap-3 py-3 px-4 rounded-xl text-white/80 hover:bg-white/10 transition-all font-medium w-full text-left"
                onClick={() => { setMenuOpen(false); setLoginOpen(true); }}>
                <LogIn size={20} className="text-blue-400" /> Log In
              </button>
              <Link to="/signup" className="flex items-center gap-3 py-3 px-4 rounded-xl text-white/80 hover:bg-white/10 transition-all font-medium"
                onClick={() => setMenuOpen(false)}>
                <UserPlus size={20} className="text-green-400" /> Sign Up
              </Link>
            </>
          )}
        </div>
      )}
    </header>
    <FounderPowerBar enabled={ccEnabled} />
    <FounderStickers enabled={ccEnabled} />
    <FriendProfileHost />
    </>
  );
};

export default Header;
